/**
 * One plain line saying what happened to an item that left active
 * consideration: "Merged into Checkout v2 — by Dana Lee on Oct 3, 2026 ·
 * same ask twice". Read only (item_outcomes, mig 860). Nothing is drawn for an
 * item still in play.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { useItemOutcomes } from '@/hooks/prioritization/useItemOutcomes';
import {
  NO_OUTCOME_RECORD,
  outcomeByline,
  outcomeLead,
  type ItemOutcome,
} from '@/lib/items/itemOutcomes';

interface ItemOutcomeLineProps {
  outcome: ItemOutcome;
  /** Drop the reason (for dense lists). */
  compact?: boolean;
  className?: string;
}

export function ItemOutcomeLine({ outcome, compact = false, className = '' }: ItemOutcomeLineProps) {
  const actorIds = useMemo(() => [outcome.actorId], [outcome.actorId]);
  const actorMap = useActorDisplayMap(actorIds);
  const actorName = outcome.actorId ? (actorMap[outcome.actorId] ?? null) : null;
  const byline = outcomeByline(outcome.at, actorName);
  const lead = outcomeLead(outcome.outcome);

  return (
    <p className={`text-sm text-[var(--text-sec)] ${className}`}>
      <span className="font-medium text-[var(--text-pri)]">{lead}</span>
      {outcome.pointerItemTitle && outcome.pointerItemId && (
        <>
          {' '}
          <Link
            to={`/prioritization/items/${outcome.pointerItemId}`}
            className="font-medium text-[var(--text-pri)] underline underline-offset-2 hover:text-[var(--text-sec)]"
          >
            {outcome.pointerItemTitle}
          </Link>
          {outcome.alsoCount > 0 && <span> (and {outcome.alsoCount} more)</span>}
        </>
      )}
      {byline && <span> — {byline}</span>}
      {!compact && outcome.reason && <span className="text-[var(--text-ter)]"> · {outcome.reason}</span>}
      {!byline && !outcome.reason && (
        <span className="text-[var(--text-ter)]"> — {NO_OUTCOME_RECORD}</span>
      )}
    </p>
  );
}

/** The outcome line for one item, or nothing when the item is still in play. */
export function ItemOutcomeBanner({ itemId, className = '' }: { itemId: string; className?: string }) {
  const { outcomes } = useItemOutcomes([itemId]);
  const outcome = outcomes.get(itemId);
  if (!outcome) return null;
  return (
    <div className={`border border-[var(--rule)] bg-[var(--surface)] px-4 py-3 ${className}`} role="status">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">What happened to this item</p>
      <ItemOutcomeLine outcome={outcome} />
    </div>
  );
}
